import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { useLocation } from 'react-router-dom';

import {
  Video,
  Wand2,
  Mail,
  BarChart3,
  Activity,
  Shuffle,
  FlaskConical,
  Shield,
  Target,
  Globe,
  KeyRound,
  Handshake,
  Share2,
  Megaphone,
  GraduationCap,
  ListVideo,
  GitCompareArrows,
  Radio,
  Workflow,
  GitFork,
} from 'lucide-react';
import { t } from '@/utils/i18n';
// Import direto do arquivo (e não do barrel 'sidebar/'): em filesystem
// case-insensitive o barrel colide com o Sidebar.tsx irmão.
import { NavGroupWithTitle } from '@/components/layout/sidebar/NavGroupWithTitle';
import { UserMenu } from '@/components/layout/header/UserMenu';
import { useCustomAuth } from '@/contexts/CustomAuthContext';

interface DrawerContentProps {
  onNavigate?: () => void;
  onOpenNovidades?: () => void;
  onOpenReplic?: () => void;
  onOpenFunnelX?: () => void;
  onOpenAdSniper?: () => void;
}

/**
 * Menu do drawer mobile.
 *
 * Espelha a Sidebar do desktop: mesmos grupos, rótulos, ícones, rotas e o mesmo
 * NavGroupWithTitle/NavItem. Os componentes são reaproveitados de propósito —
 * quando este arquivo tinha a própria cópia do NavItem, os dois menus
 * divergiram (taxonomia e ícones antigos ficaram só aqui).
 * Ao mexer na navegação, mexa em Sidebar.tsx e aqui na mesma passada.
 */
export function DrawerContent({ onNavigate, onOpenNovidades, onOpenReplic, onOpenFunnelX, onOpenAdSniper }: DrawerContentProps) {
  useLanguage();
  const location = useLocation();
  const { pathname } = location;
  const { hasPermission, isMember } = useCustomAuth();

  const handleNavClick = () => onNavigate?.();
  const canSee = (key: string) => !isMember || hasPermission(key);

  // VÍDEOS
  const videoItems = [
    { icon: Video, label: t('myVideos'), to: '/dashboard', isActive: pathname === '/dashboard', onClick: handleNavClick, featureKey: 'videos' },
    { icon: Wand2, label: t('personalizar'), to: '/personalizations', isActive: pathname.startsWith('/personalizations'), onClick: handleNavClick, featureKey: 'personalizacoes' },
    { icon: Share2, label: t('compartilhar'), to: '/shared', isActive: pathname.startsWith('/shared'), onClick: handleNavClick, featureKey: 'compartilhar' },
  ].filter((item) => canSee(item.featureKey));

  // CONVERSÃO
  const conversaoItems = [
    { icon: Mail, label: t('capturaLeads'), to: '/vsleads', isActive: pathname === '/vsleads', onClick: handleNavClick, featureKey: 'vsleads' },
    { icon: GitFork, label: translateInterfaceText("VSL Dinâmica"), to: '/vsl-dinamica', isActive: pathname.startsWith('/vsl-dinamica'), onClick: handleNavClick, featureKey: 'videos' },
  ].filter((item) => canSee(item.featureKey));

  // ANÁLISES
  const analisesItems = [
    { icon: BarChart3, label: t('métricas'), to: '/metrics', isActive: pathname === '/metrics', onClick: handleNavClick, featureKey: 'metricas' },
    { icon: Radio, label: translateInterfaceText("Tempo Real"), to: '/tempo-real', isActive: pathname === '/tempo-real', onClick: handleNavClick, featureKey: 'tempo_real' },
    { icon: Activity, label: t('rastreamento'), to: '/tracking', isActive: pathname === '/tracking', onClick: handleNavClick, featureKey: 'rastreamento' },
    { icon: GitCompareArrows, label: translateInterfaceText("Comparar VSL"), to: '/comparar-vsl', isActive: pathname === '/comparar-vsl', onClick: handleNavClick, featureKey: 'comparar_vsl' },
  ].filter((item) => canSee(item.featureKey));

  // OTIMIZAÇÃO
  const otimizacaoItems = [
    { icon: Shuffle, label: t('testeAB'), to: '/ab-test', isActive: pathname === '/ab-test' || pathname.startsWith('/ab-test/'), onClick: handleNavClick, featureKey: 'ab_test' },
    { icon: FlaskConical, label: t('hookTrack'), to: '/hook-track', isActive: pathname.includes('/hook-track'), onClick: handleNavClick, featureKey: 'hook_track' },
  ].filter((item) => canSee(item.featureKey));

  // PROTEÇÃO
  const protecaoItems = [
    { icon: Shield, label: t('cloaker'), to: '/vsl-cloaker', isActive: pathname === '/vsl-cloaker', onClick: handleNavClick, featureKey: 'cloaker' },
    { icon: Target, label: t('pixel'), to: '/pixel', isActive: pathname === '/pixel', onClick: handleNavClick, featureKey: 'pixel' },
    { icon: Globe, label: t('dominios'), to: '/domain-protection', isActive: pathname === '/domain-protection', onClick: handleNavClick, featureKey: 'dominios' },
  ].filter((item) => canSee(item.featureKey));

  // FERRAMENTAS
  const ferramentasItems = [
    { icon: Workflow, label: 'Criar Sites', to: '#', onClick: (e: React.MouseEvent) => { e.preventDefault(); handleNavClick(); onOpenReplic?.(); }, featureKey: 'sites' },
    { icon: ListVideo, label: 'Criar Quiz', to: '#', onClick: (e: React.MouseEvent) => { e.preventDefault(); handleNavClick(); onOpenFunnelX?.(); }, featureKey: 'quiz' },
  ].filter((item) => canSee(item.featureKey));

  // APRENDIZADO
  const aprendizadoItems = [
    { icon: GraduationCap, label: translateInterfaceText("Academy"), to: '/academy', isActive: pathname.startsWith('/academy'), onClick: handleNavClick, featureKey: 'academy' },
    { icon: Handshake, label: t('parceiros'), to: '/partners', isActive: pathname === '/partners', onClick: handleNavClick, featureKey: 'parceiros' },
  ].filter((item) => canSee(item.featureKey));

  // Novidades abre o modal; só existe no mobile (no desktop mora no header)
  const novidadesItems = [
    { icon: Megaphone, label: translateInterfaceText("Novidades"), to: '#', onClick: (e: React.MouseEvent) => { e.preventDefault(); handleNavClick(); onOpenNovidades?.(); } },
  ];

  return (
    <nav className="flex flex-col gap-[18px]">
      {/* Menu de perfil — no mobile este é o único acesso a conta/logout */}
      <div>
        <UserMenu variant="sidebar" />
      </div>

      <NavGroupWithTitle title={translateInterfaceText("Vídeos")} items={videoItems} />
      <NavGroupWithTitle title={translateInterfaceText("Conversão")} items={conversaoItems} />
      <NavGroupWithTitle title={translateInterfaceText("Análises")} items={analisesItems} />
      <NavGroupWithTitle title={translateInterfaceText("Otimização")} items={otimizacaoItems} />
      <NavGroupWithTitle title={translateInterfaceText("Proteção")} items={protecaoItems} />
      <NavGroupWithTitle title={translateInterfaceText("Ferramentas")} items={ferramentasItems} />
      <NavGroupWithTitle title={translateInterfaceText("Aprendizado")} items={aprendizadoItems} />
      <NavGroupWithTitle items={novidadesItems} />
    </nav>
  );
}
